import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { mockPurchaseOrderService } from '@/services/mock/MockPurchaseOrderService';
import { PurchaseOrderFilterState, PurchaseOrderMetricsSummary, PurchaseOrderRecord } from '@/types/domain/po-management';
import { PoKpiCards } from '@/components/purchase-orders/PoKpiCards';
import { DataTable, ColumnDef } from '@/components/data-display/DataTable';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { formatInr, formatDate } from '@/lib/utils';
import {
  ShoppingBag,
  RefreshCw,
  Eye,
  Pencil,
  Trash2,
  Database,
  AlertTriangle,
  Plus,
  Download,
  Loader2,
  X,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { usePermission } from '@/hooks/usePermission';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { PurchaseOrderDetailPage } from './PurchaseOrderDetailPage';

const renderPoStatus = (status: PurchaseOrderRecord['status']) => {
  let color = 'bg-primary/10 text-primary border-primary/20';
  let dot = 'bg-primary';

  switch (status) {
    case 'RELEASED':
    case 'SENT_TO_SUPPLIER':
    case 'DELIVERED':
      color = 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      dot = 'bg-emerald-500';
      break;
    case 'PENDING_RELEASE':
    case 'DRAFT':
      color = 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20';
      dot = 'bg-amber-500';
      break;
    case 'CANCELLED':
      color = 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      dot = 'bg-rose-500';
      break;
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border shadow-2xs font-poppins whitespace-nowrap w-32 justify-center ${color}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot} shrink-0`} />
      <span className="truncate">{status.replace(/_/g, ' ')}</span>
    </span>
  );
};

export const PurchaseOrdersListPage: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const [metrics, setMetrics] = useState<PurchaseOrderMetricsSummary | null>(null);

  // Deletion/Cancellation Modal State
  const [poToDelete, setPoToDelete] = useState<PurchaseOrderRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedPoForDrawer, setSelectedPoForDrawer] = useState<string | null>(null);

  const [filters, setFilters] = useState<PurchaseOrderFilterState>({
    search: '',
    status: 'ALL',
    sapStatus: 'ALL',
    plant: 'ALL',
    buyer: 'ALL',
    sortBy: 'poDate',
    sortOrder: 'desc',
    page: 1,
    pageSize: 25,
  });

  const fetchMetrics = useCallback(async () => {
    const m = await mockPurchaseOrderService.getPoMetrics();
    setMetrics(m);
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!poToDelete) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [poToDelete]);

  const fetchFn = useCallback(
    async (params: { page?: number; pageSize?: number }) => {
      const res = await mockPurchaseOrderService.getPurchaseOrders({
        ...filters,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      });
      return {
        data: res.items,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
        total: res.total,
        totalPages: Math.ceil(res.total / (params.pageSize || 25)),
        hasNextPage: (params.page || 1) * (params.pageSize || 25) < res.total,
        hasPreviousPage: (params.page || 1) > 1,
      };
    },
    [filters]
  );

  const {
    items: records,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    total,
    sentinelRef,
    retry,
  } = useInfiniteScroll<PurchaseOrderRecord>({
    fetchFn,
    params: { ...filters },
    pageSize: 25,
  });

  const handleExportCsv = () => {
    if (records.length === 0) return;
    const headers = ['PO Number', 'SAP PO', 'Vendor', 'Plant', 'Total Value (INR)', 'Status', '3-Way Match', 'Promised Delivery'];
    const rows = records.map((r) => [
      r.poNumber,
      r.sapPoNumber || 'N/A',
      `"${r.vendorName}"`,
      r.plant,
      r.totalValue,
      r.status,
      r.threeWayMatchStatus,
      r.promisedDeliveryDate,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Purchase_Orders_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmDelete = async () => {
    if (!poToDelete) return;
    try {
      setIsDeleting(true);
      await mockPurchaseOrderService.deletePurchaseOrder(poToDelete.id);
      setPoToDelete(null);
      retry();
      fetchMetrics();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to cancel purchase order.');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnDef<PurchaseOrderRecord>[] = [
    {
      header: 'PO Number',
      accessorKey: 'poNumber',
      sortable: true,
      cell: (row) => (
        <div className="space-y-0.5 font-poppins">
          <span
            onClick={() => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(row.id))}
            className="font-poppins font-bold text-primary hover:underline cursor-pointer whitespace-nowrap text-sm block"
          >
            {row.poNumber}
          </span>
          <div className="flex items-center gap-1.5 text-xs text-foreground-muted font-mono whitespace-nowrap">
            {row.sapPoNumber ? (
              <span className="text-purple-600 dark:text-purple-400 font-bold flex items-center gap-0.5">
                <Database className="h-3 w-3" /> SAP: {row.sapPoNumber}
              </span>
            ) : (
              <span>SAP: Pending</span>
            )}
            <span>•</span>
            <span>{row.plant}</span>
          </div>
        </div>
      ),
    },
    {
      header: 'Vendor & Trade Name',
      accessorKey: 'vendorName',
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col min-w-0 max-w-[200px] xl:max-w-[240px] font-poppins">
          <span className="font-bold text-foreground text-sm truncate block" title={row.vendorName}>
            {row.vendorName}
          </span>
          <span className="text-xs text-foreground-muted font-mono truncate block">
            Vendor Code: {row.vendorCode}
          </span>
        </div>
      ),
    },
    {
      header: 'Order Value',
      accessorKey: 'totalValue',
      sortable: true,
      cell: (row) => (
        <div className="font-poppins">
          <span className="font-bold text-foreground font-mono whitespace-nowrap text-sm block">
            {formatInr(row.totalValue)}
          </span>
          <span className="text-[11px] text-foreground-muted block">
            {row.items.length} Line Items
          </span>
        </div>
      ),
    },
    {
      header: '3-Way Match & Risk',
      cell: (row) => (
        <div className="font-poppins space-y-1">
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full ${
              row.threeWayMatchStatus === 'MATCHED'
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                : row.threeWayMatchStatus === 'PRICE_MISMATCH' || row.threeWayMatchStatus === 'QUANTITY_MISMATCH'
                ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20'
                : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'
            }`}
          >
            {(row.threeWayMatchStatus === 'PRICE_MISMATCH' || row.threeWayMatchStatus === 'QUANTITY_MISMATCH') && (
              <AlertTriangle className="h-3 w-3" />
            )}
            {row.threeWayMatchStatus.replace(/_/g, ' ')}
          </span>

          {row.isDelayed && (
            <div className="text-[11px] text-rose-600 dark:text-rose-400 font-bold flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Delayed ({row.delayDays}d)
            </div>
          )}
        </div>
      ),
    },
    {
      header: 'Promised Delivery',
      accessorKey: 'promisedDeliveryDate',
      cell: (row) => (
        <div className="space-y-0.5 font-poppins text-xs">
          <span className="font-semibold text-foreground block whitespace-nowrap">
            {formatDate(row.promisedDeliveryDate)}
          </span>
          <span className="text-[10px] text-foreground-muted block whitespace-nowrap">
            Ordered: {formatDate(row.poDate)}
          </span>
        </div>
      ),
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: (row) => (
        <div className="flex items-center font-poppins">
          {renderPoStatus(row.status)}
        </div>
      ),
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div
          className="flex items-center gap-1.5 justify-start shrink-0 whitespace-nowrap font-poppins min-w-[110px]"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedPoForDrawer(row.id)}
            title="View PO Dossier (Slide-over)"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/15 hover:text-primary active:scale-90 transition-all duration-200 cursor-pointer text-foreground-secondary shrink-0 inline-flex items-center justify-center group shadow-2xs hover:shadow-xs"
          >
            <Eye className="h-4 w-4 transition-transform duration-200 group-hover:scale-115" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.PURCHASE_ORDERS.EDIT(row.id))}
            title="Edit Commercial Terms"
            className="h-8 w-8 p-0 rounded-lg hover:bg-primary/10 cursor-pointer text-primary hover:text-primary-dark shrink-0 inline-flex items-center justify-center"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPoToDelete(row)}
            title="Cancel / Delete Purchase Order"
            className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-500/15 cursor-pointer shrink-0 inline-flex items-center justify-center"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <ShoppingBag className="h-7 w-7 text-primary" /> Purchase Order & Fulfillment Control Center
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Enterprise commercial purchase orders, 3-way matching, delivery fulfillment tracking, and SAP ME21N integration.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchMetrics();
              retry();
            }}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            className="rounded-xl font-bold font-poppins cursor-pointer border-border"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>

          {can('PO_CREATE') && (
            <Button
              onClick={() => navigate(ROUTES.PURCHASE_ORDERS.NEW)}
              className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Create Purchase Order
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards Strip */}
      {metrics && <PoKpiCards metrics={metrics} />}

      {/* Main Ledger Card with Squeezed Status Tabs & Search */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            {/* Squeezed Status Tabs */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'ALL', label: 'All Orders' },
                { key: 'RELEASED', label: 'Released' },
                { key: 'PENDING_RELEASE', label: 'Pending Release' },
                { key: 'DELIVERED', label: 'Delivered' },
                { key: 'DRAFT', label: 'Draft' },
                { key: 'CANCELLED', label: 'Cancelled' },
              ].map((tab) => {
                const isActive = filters.status === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilters((prev) => ({ ...prev, status: tab.key }))}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer font-poppins ${
                      isActive
                        ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                        : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-4 overflow-x-auto">
          <DataTable
            data={records}
            columns={columns}
            isLoading={isLoading}
            pageSize={25}
            totalCount={total}
            infiniteScroll={true}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            sentinelRef={sentinelRef}
            searchPlaceholder="Search purchase orders..."
            fixedHeight={true}
            containerHeightClass="max-h-[580px] overflow-y-auto overflow-x-auto custom-scrollbar"
            emptyTitle="No Purchase Orders Found"
            emptyDescription="There are no purchase orders matching your current filter criteria."
            onRowClick={(row) => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(row.id))}
          />
        </CardContent>
      </Card>

      {/* Delete / Cancel Confirmation Modal (Mounted via createPortal to document.body) */}
      {poToDelete &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in"
            onClick={() => !isDeleting && setPoToDelete(null)}
          >
            <div
              className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-rose-600">
                  <AlertTriangle className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    Cancel & Delete Purchase Order?
                  </h3>
                </div>
                <button
                  onClick={() => !isDeleting && setPoToDelete(null)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Order Details Box */}
              <div className="rounded-xl bg-muted/40 border border-border p-3.5 space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    PO Number
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {poToDelete.poNumber}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Supplier
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[200px] text-right">
                    {poToDelete.vendorName}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-foreground-muted uppercase tracking-wider text-[10px]">
                    Committed Value
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {formatInr(poToDelete.totalValue)}
                  </span>
                </div>
              </div>

              <p className="text-xs text-foreground-secondary leading-relaxed">
                Are you sure you want to cancel and remove this purchase order? Associated GRN deliveries and SAP synchronization will be permanently revoked.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isDeleting}
                  onClick={() => setPoToDelete(null)}
                  className="cursor-pointer font-bold rounded-xl font-poppins"
                >
                  Keep Order
                </Button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleConfirmDelete}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-extrabold text-xs shadow-lg shadow-rose-600/30 transition-all cursor-pointer disabled:opacity-50 font-poppins"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Cancelling...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Cancel PO</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Right Slide-Over Curtain Drawer for PO Dossier */}
      <SlideOverDrawer
        isOpen={Boolean(selectedPoForDrawer)}
        onClose={() => setSelectedPoForDrawer(null)}
        title="Purchase Order Dossier"
        subtitle="Commercial terms, delivery fulfillment, three-way match, and SAP audit"
        fullPageUrl={selectedPoForDrawer ? ROUTES.PURCHASE_ORDERS.DETAILS(selectedPoForDrawer) : undefined}
      >
        {selectedPoForDrawer && (
          <PurchaseOrderDetailPage
            recordId={selectedPoForDrawer}
            isDrawer={true}
            onClose={() => setSelectedPoForDrawer(null)}
          />
        )}
      </SlideOverDrawer>
    </div>
  );
};
